import React, {useEffect, useState} from 'react';
import {Button, Checkbox, Col, DatePicker, Input, message, Row, Select, Table} from 'antd';
import {
    ExportControllerApi,
    Pageable,
    PageTestExecutionDto,
    TestExecutionControllerApi,
    TestExecutionDto,
    TestResultDto
} from "../api";
import {useKeycloakHeader} from "../config/Config";
import {useKeycloak} from "@react-keycloak/web";
import {hasRole, ROLES} from "../components/misc/RolesHelper";
import MissingAuthorizationPage from "./error/MissingAuthorizationPage";
import {formatDateTime, getErrorMessage, renderStatusAmpel} from "../utils/Utils";
import TestResultModal from "../components/modal/testExecution/TestResultModal";
import {
    CloseOutlined,
    DownloadOutlined,
    CheckOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
    EyeOutlined
} from "@ant-design/icons";
import {downloadFile} from "../utils/FileDownload";
import {COLORS} from "../utils/Colors";
import { AlignType } from 'rc-table/lib/interface'
import {Link} from "react-router-dom";


const {RangePicker} = DatePicker;
const {Option} = Select;

const TestExecutionOverview = () => {
    const {keycloak} = useKeycloak();
    const [testExecutionDtos, setTestExecutionDtos] = useState<TestExecutionDto[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10); // Standard-Seitengröße
    const [searchTestId, setSearchTestId] = useState<number | undefined>(undefined); // Der Suchbegriff
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedTestResult, setSelectedTestResult] = useState<TestResultDto>();
    const [dateRange, setDateRange] = useState<[string, string] | undefined>(undefined);
    const [isUsedOptional, setIsUsedOptional] = useState(undefined);
    const [selectedTestExecutions, setSelectedTestExecutions] = useState<number[]>([]);

    const headers = useKeycloakHeader();
    const testExecutionControllerApi = new TestExecutionControllerApi();
    const exportControllerApi = new ExportControllerApi();

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'System',
            dataIndex: ['result', 'system', 'name'],
            key: 'result.system.name'
        },
        {
            title: 'Test-ID',
            dataIndex: ['result', 'testId'],
            key: 'result.testId',
            render: (testId) => (
                <div>
                    <Link to={process.env.PUBLIC_URL + `/edit/test/${testId}`} style={{marginRight: '8px'}}>
                        {testId}
                    </Link>
                </div>
            ),
        },
        {
            title: 'Test',
            dataIndex: ['result', 'name'],
            key: 'result.name'
        },
        {
            title: 'Beschreibung',
            dataIndex: ['result', 'description'],
            key: 'result.description'
        },
        {
            title: 'Start',
            dataIndex: 'startTs',
            key: 'startTs',
        },
        {
            title: 'Ende',
            dataIndex: 'endTs',
            key: 'endTs',
        },
        {
            title: <ReloadOutlined title={"Unverbrauchter Testlauf"}/>,
            dataIndex: ['result', 'used'],
            key: 'result.used',
            width: '3%',
            render: (used: boolean) => (
                <span>{used ? <CloseOutlined style={{color: "red"}} /> : <CheckOutlined style={{color: "green"}} />}</span>
            ),
        },
        {
            title: 'Variablen',
            dataIndex: ['result', 'runtimeVariables'],
            key: 'result.variables',
            render: (text, record) => {
                // Listen zusammenführen
                const combinedVariables = [
                    ...(record.result.predefinedVariables || []),
                    ...(record.result.runtimeVariables || [])
                ];

                // Variablen als Text anzeigen
                return (
                    <span>
                {combinedVariables.map(variable => variable.name).join(', ')}
            </span>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'runStatus',
            key: 'runStatus',
            width: '5%',
            render: (text, record) => (
                <span>{renderStatusAmpel(record.runStatus)}</span>
            ),
        },
        {
            title: <InfoCircleOutlined />,
            dataIndex: 'info',
            align: 'center' as AlignType,
            key: 'info',
            width: '3%',
            render: (text, record) => (
                <Button style={{color: COLORS.btcPrimaryMediumBlue}} size={"small"} onClick={(e) => handleRowClick(record)}><EyeOutlined /></Button>
            ),
        },
        {
            title: <DownloadOutlined />,
            dataIndex: 'export',
            key: 'export',
            align: 'center' as AlignType,
            width: '3%',
            render: (text, record) => (
                <Checkbox onChange={(e) => updateCheckboxId(record.id)}></Checkbox>
            ),
        },
    ];

    const fetchData = (page: number, size: number) => {
        const pageable: Pageable = {
            page: page - 1, // Spring verwendet 0-basierte Seitenzahlen
            size: size ? size : 10,
            sort: ['id;desc'] // Beispiel für Sortierung als Array
        } as Pageable;

        testExecutionControllerApi.getTestRunsWithCriteria(
            pageable.size ? pageable.size : 10,
            pageable.page ? pageable.page : 0,
            pageable.sort ? pageable.sort : ['id;asc'],
            searchTestId,
            dateRange ? dateRange[0] : undefined,
            dateRange ? dateRange[1] : undefined,
            isUsedOptional,
            headers
        )
            .then((axiosResponse) => {
                const axiosData: PageTestExecutionDto = axiosResponse.data
                if (axiosData.content) {
                    const testExecutionDtos: Array<TestExecutionDto> = axiosData.content;
                    const withFormattedDate = testExecutionDtos.map((testExecution: TestExecutionDto) => ({
                        ...testExecution,
                        startTs: formatDateTime(testExecution.startTs),
                        endTs: formatDateTime(testExecution.endTs)
                    }));
                    setTestExecutionDtos(withFormattedDate);
                }

                if (axiosData.totalElements) {
                    setTotalItems(axiosData.totalElements);
                }
            })
            .catch((error) => {
                let msg = "Fehler beim Abrufen der Daten: ";
                message.error(msg + getErrorMessage(error));
            });
    };

    useEffect(() => {
        fetchData(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    let roles = [
        ROLES.GLOW_ADMIN,
        ROLES.GLOW_TEST_DEVELOPER,
        ROLES.GLOW_TEST_DESIGNER,
        ROLES.GLOW_TEST_MANAGER
    ];
    const hasRoleForPage = hasRole(roles, keycloak);
    if (!hasRoleForPage) {
        return (<MissingAuthorizationPage roles={roles}/>);
    }

    const handleRowClick = (record) => {
        setSelectedTestResult(record.result);
        setModalVisible(true);
    };

    const handleSearch = () => {
        fetchData(currentPage, pageSize);
    };

    const updateCheckboxId = (testExecutionId: number) => {
        // Erstelle eine Kopie des aktuellen Zustands
        let testExecutions = [...selectedTestExecutions];

        // Überprüfe, ob die ID bereits im Array vorhanden ist
        const index = testExecutions.indexOf(testExecutionId);

        if (index !== -1) {
            // Wenn die ID vorhanden ist, entferne sie
            testExecutions.splice(index, 1);
        } else {
            // Wenn die ID nicht vorhanden ist, füge sie hinzu
            testExecutions.push(testExecutionId);
        }

        // Aktualisiere den State mit dem neuen Array
        setSelectedTestExecutions(testExecutions);
    }

    const doExportCsv = () => {
        if (selectedTestExecutions.length > 0) {
            exportControllerApi.exportTestExecutionsToCsv(selectedTestExecutions, headers)
                .then(response => {
                    downloadFile(response.data, "test_executions_", "csv");
                    message.success("Datei erfolgreich heruntergeladen!");
                })
                .catch(error => {
                    message.error("Fehler beim Herunterladen einer Datei!");
                });
        } else {
            message.error("Es wurde kein Testergebnis ausgewählt!");
        }
    };

    const doExportJson = () => {
        if (selectedTestExecutions.length > 0) {
            exportControllerApi.exportTestExecutionsJson(selectedTestExecutions, headers)
                .then(response => {
                    downloadFile(response.data, "test_executions_", "json");
                    message.success("Datei erfolgreich heruntergeladen!");
                })
                .catch(error => {
                    message.error("Fehler beim Herunterladen einer Datei!");
                });
        } else {
            message.error("Es wurde kein Testergebnis ausgewählt!");
        }
    };

    return (
        <div>
            <h1>Testausführungen</h1>
            <Row gutter={16} style={{marginBottom: 16, alignItems: 'center'}}>
                <Col>
                    Test-ID:
                </Col>
                <Col span={3}>
                    <Input
                        placeholder="Nach Test-ID suchen"
                        onChange={(e) => {
                            const value = e.target.value;
                            setSearchTestId(value ? parseInt(value, 10) : undefined); // Wenn leer, auf undefined setzen
                        }}
                        value={searchTestId !== undefined ? searchTestId.toString() : ''} // Sicherstellen, dass value als String dargestellt wird
                    />
                </Col>

                <Col>
                    Zeitraum:
                </Col>
                <Col span={6}>
                    <RangePicker
                        format="YYYY-MM-DD"
                        placeholder={['Startdatum', 'Endedatum']}
                        onChange={(dates) => {
                            let date1 = "";
                            let date2 = "";
                            if (dates) {
                                if (dates[0]) {
                                    date1 = dates[0].format("YYYY-MM-DD");
                                }
                                if (dates[1]) {
                                    date2 = dates[1].format("YYYY-MM-DD");
                                }
                            }
                            setDateRange(dates ? [date1, date2] : undefined);
                        }}
                        style={{width: '100%'}}
                    />
                </Col>

                <Col>
                    Verwendet?:
                </Col>
                <Col span={4}>
                    <Select
                        placeholder="Wähle Ja, Nein oder Beides"
                        onChange={(value) => setIsUsedOptional(value)} // Setze den Status entsprechend der Auswahl
                        allowClear // Ermöglicht das Zurücksetzen der Auswahl
                        style={{width: '100%'}}
                        value={isUsedOptional}
                    >
                        <Option value={undefined}>Beides</Option>
                        <Option value={true}>Ja</Option>
                        <Option value={false}>Nein</Option>
                    </Select>
                </Col>

                <Col span={2}>
                    <Button type="primary" onClick={handleSearch} style={{width: "100%"}}>Suchen</Button>
                </Col>
                <Col span={2}>
                    <Button type="primary" onClick={doExportCsv}  style={{width: "100%"}} icon={<DownloadOutlined/>}>CSV</Button>
                </Col>
                <Col span={2}>
                    <Button type="primary" onClick={doExportJson}  style={{width: "100%"}} icon={<DownloadOutlined/>}>Json</Button>
                </Col>
            </Row>
            <Table
                dataSource={testExecutionDtos}
                columns={columns}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalItems,
                    showSizeChanger: true,
                    locale: {
                        items_per_page: "/ Seite"
                    }
                }}
                locale={{ emptyText: 'Keine Daten vorhanden' }}
                onChange={handleTableChange}
                rowKey="id"
            />
            {selectedTestResult && (
                <TestResultModal visible={modalVisible} testResultId={selectedTestResult.id}
                                 onClose={() => setModalVisible(false)}/>
            )}
        </div>
    );
};

export default TestExecutionOverview;